import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Typography, Box, Chip, Divider, Avatar } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { newsApi, News } from '../api/news';
import VisibilityIcon from '@mui/icons-material/Visibility';

export default function NewsDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [news, setNews] = useState<News | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      newsApi.getBySlug(slug).then((data) => {
        setNews(data);
        setLoading(false);
      });
    }
  }, [slug]);

  if (loading || !news) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      {news.coverImage && (
        <Box
          component="img"
          src={news.coverImage}
          alt={news.title}
          sx={{ width: '100%', height: 400, objectFit: 'cover', borderRadius: 2, mb: 4 }}
        />
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {news.tags.map((tag) => (
          <Chip key={tag} label={tag} color="primary" />
        ))}
      </Box>

      <Typography variant="h2" gutterBottom>
        {news.title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {news.author.avatar && <Avatar src={news.author.avatar} />}
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {news.author.firstName} {news.author.lastName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {new Date(news.publishedAt || news.createdAt).toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <VisibilityIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {news.views}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Box
        sx={{
          '& p': { mb: 2, lineHeight: 1.8 },
          '& h1, & h2, & h3': { mt: 4, mb: 2 },
          '& ul, & ol': { mb: 2, pl: 3 },
          '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
          '& code': {
            bgcolor: 'background.paper',
            p: 0.5,
            borderRadius: 1,
            fontFamily: 'monospace',
          },
          '& pre': {
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
          },
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{news.content}</ReactMarkdown>
      </Box>
    </Container>
  );
}
